import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const MAX_ATTEMPTS = 6;
const INTERVAL = 2000;

export default function FounderSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const [info, setInfo] = useState(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!sessionId) {
      setState("failed");
      return;
    }

    let attempts = 0;
    let stop = false;

    const poll = async () => {
      if (stop) return;
      try {
        const { data } = await api.get(`/founders/checkout/status/${sessionId}`);
        setInfo(data);
        if (data.payment_status === "paid") {
          setState("success");
          await refreshUser();
          return;
        }
        if (data.status === "expired") {
          setState("failed");
          return;
        }
      } catch {
        /* keep polling */
      }
      attempts += 1;
      if (attempts >= MAX_ATTEMPTS) {
        setState("timeout");
        return;
      }
      setTimeout(poll, INTERVAL);
    };
    poll();
    return () => { stop = true; };
  }, [sessionId, refreshUser]);

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-xl flex-col items-center justify-center px-4 py-20 text-center" data-testid="founder-success-page">
      {state === "checking" && (
        <>
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <h1 className="mt-6 text-2xl font-[720] text-foreground">Confirming your payment…</h1>
          <p className="mt-2 text-muted-foreground">Hang tight, this only takes a moment.</p>
        </>
      )}

      {state === "success" && (
        <div data-testid="founder-success-confirmed">
          <CheckCircle2 className="mx-auto h-14 w-14 text-primary" />
          <h1 className="mt-6 text-3xl font-[720] text-foreground">Welcome aboard! 🦦</h1>
          <p className="mt-3 text-muted-foreground">
            Your <span className="font-[640] text-foreground">{info?.founder_name || "Founder"}</span> membership is active.
            Your Founding Member badge and benefits are now on your account
            {" "}— check your email for your welcome note and any team license keys.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/dashboard/account"><Button data-testid="founder-success-account">View my account</Button></Link>
            <Link to="/pricing"><Button variant="secondary">Back to pricing</Button></Link>
          </div>
        </div>
      )}

      {(state === "failed" || state === "timeout") && (
        <div data-testid="founder-success-failed">
          <XCircle className="mx-auto h-14 w-14 text-muted-foreground" />
          <h1 className="mt-6 text-2xl font-[720] text-foreground">
            {state === "timeout" ? "Still processing" : "Payment not completed"}
          </h1>
          <p className="mt-3 text-muted-foreground">
            {state === "timeout"
              ? "Your payment is taking longer than expected. If you completed checkout, your benefits will appear on your account shortly — check your email for confirmation."
              : "We couldn't confirm a completed payment. If you were charged, please contact support."}
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/pricing"><Button>Back to pricing</Button></Link>
            <Link to="/contact"><Button variant="secondary">Contact support</Button></Link>
          </div>
        </div>
      )}
    </div>
  );
}
